import { Box } from '@mui/material';
import {
  DataGridPremium,
  GridCallbackDetails,
  GridRowId,
  GridRowSelectionModel,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { AddButton, CustomSortableColumn, NoTableRowsOverlay } from '@retail/components';
import { AssortmentPrices, AssortmentView } from '@retail/my-assortment/types';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import { useMyAssortmentTFunction } from '@retail/my-assortment/i18n';
import { dataGridHorizontalPaddingSmallClass } from '@shared/styles';
import { useAssortmentItemActions } from './AssortmentItems';
import { useGroupedRowClassName } from '@retail/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAssortmentPricesColumns } from './useAssortmentPricesColumns';
import { ExternalItemSource } from '@retail/products/components';
import { OrgUnitType } from '@retail/mgpris/config-types';

interface Props {
  items: AssortmentPrices[];
  emptyAssortment: boolean;
  selectedItem: AssortmentPrices | null;
  loadingFetchItems: boolean;
  includeVat: boolean;
  selectedView: AssortmentView;
  isDeleteEnabled: boolean;
  isPriceCalculationEnabled: boolean;
  isPriceSuggestionEnabled: boolean;
  orgUnitType: OrgUnitType;
  showMainSupplierName?: boolean;
  externalItemSource: ExternalItemSource;
  toggleAddItemsDialog?: () => void;
  viewCalculusCard?: (mgSupplierItemId: number) => void;
  viewProductCard: (mgSupplierItemId: number) => void;
  editItem?: (item: AssortmentPrices) => void;
  editPrices?: (item: AssortmentPrices) => void;
  deleteItem?: (mgSupplierItemId: number) => void;
}
const itemIdSelector = (item: AssortmentPrices) => item.mgSupplierItemId;

export function AssortmentPricesItemsTable({
  items,
  emptyAssortment,
  selectedItem,
  loadingFetchItems,
  includeVat,
  selectedView,
  isDeleteEnabled,
  isPriceCalculationEnabled,
  isPriceSuggestionEnabled,
  orgUnitType,
  showMainSupplierName = false,
  externalItemSource,
  toggleAddItemsDialog,
  viewCalculusCard,
  viewProductCard,
  editItem,
  editPrices,
  deleteItem,
}: Props) {
  const t = useMyAssortmentTFunction();
  const {
    i18n: { language },
  } = useTranslation();

  const { itemsChecked, checkItems } = useAssortmentItemActions();

  const onRowsSelectionHandler = (
    selectionModel: GridRowSelectionModel,
    details: GridCallbackDetails
  ) => {
    if (details.reason === 'singleRowSelection') {
      checkItems(Array.from(selectionModel.ids) as number[]);
    } else if (itemsChecked.length !== items.length) {
      checkItems(items.map(itemIdSelector));
    } else {
      checkItems([]);
    }
  };

  const selectionModel: GridRowSelectionModel = {
    type: 'include',
    ids: new Set<GridRowId>(itemsChecked),
  };

  const apiRef = useGridApiRef();
  const getRowClassName = useGroupedRowClassName<AssortmentPrices>({
    apiRef,
    getGroupId: (row) => row.mgItemNumber,
    getSelectionId: (row) => row.mgSupplierItemId,
    selectedId: selectedItem?.mgSupplierItemId,
  });

  const columnConfigs = useAssortmentPricesColumns({
    includeVat,
    isPriceCalculationEnabled,
    externalItemSource,
    rowIdForCalculusCard: viewCalculusCard,
    rowIdForProductCard: viewProductCard,
    editItem,
    editPrices,
    deleteItem,
  });

  const columns = useMemo(
    () =>
      columnConfigs
        .filter(({ show }) =>
          show({
            selectedView,
            showBracketPriceColumns: orgUnitType.channel === 'WHOLESALE',
            orgUnitType,
            isPriceSuggestionEnabled,
            showMainSupplierName,
          })
        )
        .map(({ getColumn }) => getColumn()),
    [columnConfigs, selectedView, orgUnitType, isPriceSuggestionEnabled, showMainSupplierName]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative' }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium<AssortmentPrices>
          apiRef={apiRef}
          rows={items}
          columns={columns}
          getRowId={itemIdSelector}
          getRowClassName={getRowClassName}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          className={dataGridHorizontalPaddingSmallClass}
          disableColumnReorder
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooter
          rowHeight={65}
          checkboxSelection={isDeleteEnabled}
          loading={loadingFetchItems}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={onRowsSelectionHandler}
          sx={{
            '& .MuiDataGrid-iconButtonContainer': {
              visibility: 'visible !important',
              width: 'auto !important',
            },
            '& .MuiDataGrid-sortIcon': { opacity: '1 !important' },
          }}
          slots={{
            columnHeaderSortIcon: (props) => <CustomSortableColumn sorting={props.direction} />,
            noRowsOverlay: () =>
              emptyAssortment ? (
                <NoTableRowsOverlay
                  noRowsText={t('myAssortment.noItems')}
                  addAction={
                    toggleAddItemsDialog && (
                      <AddButton onClick={toggleAddItemsDialog}>
                        {t('myAssortment.actions.addItems')}
                      </AddButton>
                    )
                  }
                />
              ) : (
                <NoTableRowsOverlay noRowsText={t('myAssortment.filters.noMatchOnSearch')} />
              ),
          }}
        />
      </Box>
    </Box>
  );
}
